import { useState } from 'react';
import { Alert, Button, Dialog, DialogContent } from '@mui/material';
import EditAdditionalLocation from './EditAdditionalLocation';
import DialogActionBar from './DialogActionBar';
import { validateLocationData, isLocationDataValid } from '../../../utils/operationValidation';
import { useUser } from '../../../hooks/useUser';
import { CreateOperationData, Operation, OperationLocation, updateOperation } from '@networkRequest/operationsService';

interface AddAdditionalLocationModalProps {
  open: boolean;
  onClose: () => void;
  operation: Operation;
  onOperationUpdate: (op: Operation) => void;
}

export default function AddAdditionalLocationModal({
  open,
  onClose,
  operation,
  onOperationUpdate,
}: AddAdditionalLocationModalProps) {
  const { user } = useUser();

  const [formData, setFormData] = useState<OperationLocation>({
    id: Date.now().toString(),
    name: '',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    zip: '',
    primary: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFormValid = () => {
    return isLocationDataValid(formData, false); // false = additional location
  };

  const handleSave = async () => {
    if (!user?.accessToken || !operation?.id) return null;

    const validationErrors = validateLocationData(formData, false); // false = additional location

    if (validationErrors.length > 0) {
      setError(`Please fix the following errors: ${validationErrors.join(', ')}`);
      return;
    }

    setLoading(true);
    // setError(null);

    try {
      // Create new location attributes array with all existing locations plus the new one
      const allLocationAttributes = [
        // Existing locations
        ...operation.operation_locations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          street_address_1: loc.street_address_1,
          street_address_2: loc.street_address_2,
          city: loc.city,
          state: loc.state,
          zip: loc.zip,
          primary: loc.primary
        })),
        // New additional location
        {
          name: formData.name,
          street_address_1: formData.street_address_1,
          street_address_2: formData.street_address_2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          primary: false
        }
      ];

      const updateData: Partial<CreateOperationData> = {
        operation_locations_attributes: allLocationAttributes
      };

      const result = await updateOperation(user.accessToken, operation.id, updateData);

      if (result.error) {
        setError(result.error);
        return;
      }

      // Update local operation state with the updated data
      if (result.data) {
        onOperationUpdate(result.data);
      }

      // Close modal on successful save
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add additional location');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setLoading(false);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '900px',
          maxWidth: '900px',
          height: 'auto',
          maxHeight: '90vh',
          pt: 4,
          px: 3,
          pb: 2
        },
      }}
    >
      <DialogContent sx={{ p: 0, mb: 3 }}>
        <EditAdditionalLocation
          title="Add Additional Location"
          locationData={formData}
          onLocationDataChange={(field: string, value: string) => {
            setFormData((prev) => ({
              ...prev,
              [field]: value
            }));
          }}
          backgroundColor='common/white_states/main'
        />
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActionBar sx={{ paddingRight: 4 }}>
        <Button onClick={handleCancel} variant="outlined" size="medium">
          Cancel
        </Button>
        <Button
          onClick={() => handleSave()}
          variant="contained"
          size="medium"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Adding...' : 'Add Location'}
        </Button>
      </DialogActionBar>
    </Dialog>
  );
}